import { ObjectKeys } from '@/lib/utils';
import type {
  FieldValue,
  IntegrationManifestService,
} from '@pigello/pigello-matrix';

export function getRequiredActivationFields(
  service: IntegrationManifestService
) {
  const requiredFields: string[] = [];

  for (const [name, settings] of Object.entries(service.activation_variables)) {
    if (settings.allow_null === false && settings.default_value === null) {
      requiredFields.push(name);
    }
  }

  return requiredFields;
}

export function removeNonRequiredActivationFields(
  activationVariables: FieldValue<Record<string, unknown>>,
  requiredFields: string[]
) {
  const cleanActivationCopy = { ...activationVariables };
  for (const field in activationVariables) {
    if (!requiredFields.includes(field)) {
      activationVariables[field] = undefined;
    }
  }
  return cleanActivationCopy;
}

export function getDirtyValues<
  DirtyFields extends Record<string, unknown>,
  Values extends Record<keyof DirtyFields, unknown>,
>(dirtyFields: DirtyFields, values: Values): Partial<typeof values> {
  const dirtyValues = Object.keys(dirtyFields).reduce((prev, key) => {
    // Unsure when RFH sets this to `false`, but omit the field if so.
    if (!dirtyFields[key]) return prev;

    return {
      ...prev,
      [key]:
        typeof dirtyFields[key] === 'object'
          ? getDirtyValues(
              dirtyFields[key] as DirtyFields,
              values[key] as Values
            )
          : values[key],
    };
  }, {});

  return dirtyValues;
}

/**
 * Recursively extracts only the dirty fields from form values.
 * * - Returns `undefined` if nothing in the node is dirty.
 * - Preserves array indices (returns `undefined` at indices that are not dirty).
 * - Removes empty objects.
 */
export function getDirtyFields<
  TData extends Record<string, any> | unknown[],
  TDirty extends Record<string, any> | boolean | unknown[],
>(formValues: TData, dirtyFields: TDirty): Partial<TData> | undefined {
  // 1. If dirtyFields is strictly true, the specific field (leaf) is dirty.
  // Return the value immediately.
  if (dirtyFields === true) {
    return formValues as Partial<TData>;
  }

  // 2. If dirtyFields is false/null/undefined, nothing here is dirty.
  if (!dirtyFields || typeof dirtyFields !== 'object') {
    return undefined;
  }

  // 3. Handle Arrays
  if (Array.isArray(dirtyFields) && Array.isArray(formValues)) {
    const result = dirtyFields.map((dirtyItem, index) => {
      return getDirtyFields(formValues[index], dirtyItem);
    });

    // If the array contains only undefined (no dirty items), return undefined
    // to prune the empty array from the parent.
    if (result.every((item) => item === undefined)) {
      return undefined;
    }

    return result as unknown as Partial<TData>;
  }

  // 4. Handle Objects
  // We iterate over the keys of dirtyFields (not formValues),
  // because we only care about what RHF flagged.
  const dirtyObjKeys = Object.keys(dirtyFields);
  const resultObj: Record<string, unknown> = {};

  for (const key of dirtyObjKeys) {
    const dirtyValue = dirtyFields[key as keyof TDirty];
    const formValue = (formValues as Partial<Record<string, unknown>>)?.[key];

    const dirtyChild = getDirtyFields(formValue as any, dirtyValue as any);

    if (dirtyChild !== undefined) {
      resultObj[key] = dirtyChild;
    }
  }

  // If object is empty, return undefined to prune it.
  if (Object.keys(resultObj).length === 0) {
    return undefined;
  }

  return resultObj as Partial<TData>;
}

/**
 * Recursively removes properties from 'obj' that match 'defaults'.
 * Arrays are treated as atomic values (compared entirely, not element-wise).
 */
export function omitDefaults<T extends Record<string, any>>(
  obj: T,
  defaults: T
): Partial<T> {
  // 1. Helper for loose deep comparison (or replace with fast-deep-equal)
  const isEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);

  // 2. Handle Primitives and Arrays immediately
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }

  // 3. Iterate over keys
  const result: any = {};

  for (const key of ObjectKeys(obj)) {
    const value = obj[key];
    const defaultValue = defaults ? defaults[key] : undefined;

    // A. Exact Match: Skip (Omit)
    if (isEqual(value, defaultValue)) {
      continue;
    }

    // B. Nested Objects (non-array): Recurse
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      typeof defaultValue === 'object' &&
      defaultValue !== null
    ) {
      const difference = omitDefaults(value, defaultValue);

      // Only keep the parent key if the child object isn't empty
      if (Object.keys(difference).length > 0) {
        result[key] = difference;
      }
    }
    // C. Value Differs: Keep it
    else {
      result[key] = value;
    }
  }

  return result;
}
type Integration =
  | 'SCRIVE'
  | 'MICROSOFT_BOOKINGS'
  | 'ECO_POST'
  | 'SIE4'
  | 'Gl07'
  | 'Gl02'
  | 'FORTNOX'
  | 'AUTOGIRO'
  | 'AUTOGIRO_DEV'
  | 'VISMA_EEKONOMI'
  | 'PS_FINANCE'
  | 'FAST_DOC'
  | 'SCB_KPI'
  | 'SCB_ADDITIONAL_KPI'
  | 'INFOMETRIC'
  | 'PIGELLO_PAYMENTS_CREATE'
  | 'UPPSALA_BOSTADSFORMEDLING';

type IntegrationIdMapType = {
  [applicationId: string]: {
    [serviceId: string]: Integration;
  };
};
export const IntegrationIdMap: IntegrationIdMapType = {
  'e0ecd748-9aa9-472c-9b40-8ac69329ce54': {
    '48e33a95-e312-58ae-9061-e67e19c8d9a7': 'FAST_DOC',
  },
  '4c52aeff-a913-407d-9f4b-b6087b49490b': {
    'acd93c35-17c9-5cb7-93c7-d2f9a998344e': 'SCRIVE',
  },
  'ff7c49c6-9dee-4bb1-833f-dcd00c589207': {
    '58a1ef2e-8738-5bb7-a4e0-72a492f9e8db': 'SIE4',
    '58edb76d-fa92-5e8a-a7c0-4ad1ea6bff18': 'Gl02',
    'd88f61f4-37ac-5915-bd70-d358684382f6': 'Gl07',
  },
  '179cc7f5-9491-4e3c-8108-91f7e592a683': {
    'bfbc5035-2fcf-5aad-9863-1dad414411ee': 'FORTNOX',
  },
  '4ab874a5-b540-495c-bcc2-2ac7b4d3268d': {
    '464cf1e8-320d-521d-a393-8073c59995ad': 'VISMA_EEKONOMI',
  },
  '8a9f2b67-cdce-4e56-98be-31a152c0d9cb': {
    '9aeb3ef9-2ae8-5a85-8ff3-0d68ca9341aa': 'AUTOGIRO',
  },
  '72afc6a4-c437-4f57-a709-3273b71c7581': {
    '9aeb3ef9-2ae8-5a85-8ff3-0d68ca9341aa': 'AUTOGIRO_DEV',
  },
  'f74d684c-6b20-4f9e-b07c-9fed2692be2d': {
    '2c67404f-6ad2-5e99-8afe-14afc37fc26f': 'PS_FINANCE',
  },
  '669e7ff7-a4d7-4f9d-801b-9c1e95dfa6ec': {
    '28fe2f5a-86c1-55eb-932c-6fbf49bd988e': 'SCB_KPI',
    'fb71db5b-6495-522a-84f2-21b59aeb4b19': 'SCB_ADDITIONAL_KPI',
  },
  'fabe15b5-11ee-45f6-8263-c623059babc4': {
    '1ab3cae0-c4fd-500f-8b74-b7f039f761aa': 'UPPSALA_BOSTADSFORMEDLING',
  },
  '7b01716f-51dc-427b-96a2-27f4b2f0c23f': {
    '5a4259f4-59dc-51eb-b033-c519387161fc': 'MICROSOFT_BOOKINGS',
  },
} as const;

type IIntegrationServiceDataMap = {
  [K in Integration]?: {
    applicationId: string;
    serviceId?: string;
  };
};

export const IntegrationServiceDataMap = {
  FAST_DOC: {
    applicationId: 'e0ecd748-9aa9-472c-9b40-8ac69329ce54',
    serviceId: '48e33a95-e312-58ae-9061-e67e19c8d9a7',
  },
  SCRIVE: {
    applicationId: '4c52aeff-a913-407d-9f4b-b6087b49490b',
    serviceId: 'acd93c35-17c9-5cb7-93c7-d2f9a998344e',
  },
  FORTNOX: {
    applicationId: '179cc7f5-9491-4e3c-8108-91f7e592a683',
    serviceId: 'bfbc5035-2fcf-5aad-9863-1dad414411ee',
  },
  VISMA_EEKONOMI: {
    applicationId: '4ab874a5-b540-495c-bcc2-2ac7b4d3268d',
    serviceId: '464cf1e8-320d-521d-a393-8073c59995ad',
  },
  AUTOGIRO: {
    applicationId: '8a9f2b67-cdce-4e56-98be-31a152c0d9cb',
    serviceId: '9aeb3ef9-2ae8-5a85-8ff3-0d68ca9341aa',
  },
  AUTOGIRO_DEV: {
    applicationId: '72afc6a4-c437-4f57-a709-3273b71c7581',
    serviceId: '9aeb3ef9-2ae8-5a85-8ff3-0d68ca9341aa',
  },
  SIE4: {
    applicationId: 'ff7c49c6-9dee-4bb1-833f-dcd00c589207',
    serviceId: '58a1ef2e-8738-5bb7-a4e0-72a492f9e8db',
  },
  Gl02: {
    applicationId: 'ff7c49c6-9dee-4bb1-833f-dcd00c589207',
    serviceId: '58edb76d-fa92-5e8a-a7c0-4ad1ea6bff18',
  },
  Gl07: {
    applicationId: 'ff7c49c6-9dee-4bb1-833f-dcd00c589207',
    serviceId: 'd88f61f4-37ac-5915-bd70-d358684382f6',
  },
  PS_FINANCE: {
    applicationId: 'f74d684c-6b20-4f9e-b07c-9fed2692be2d',
    serviceId: '2c67404f-6ad2-5e99-8afe-14afc37fc26f',
  },
  SCB_KPI: {
    applicationId: '669e7ff7-a4d7-4f9d-801b-9c1e95dfa6ec',
    serviceId: '28fe2f5a-86c1-55eb-932c-6fbf49bd988e',
  },
  SCB_ADDITIONAL_KPI: {
    applicationId: '669e7ff7-a4d7-4f9d-801b-9c1e95dfa6ec',
    serviceId: 'fb71db5b-6495-522a-84f2-21b59aeb4b19',
  },
  INFOMETRIC: {
    applicationId: 'e502aca0-8948-48a9-8dc7-47b99bb8020e',
    serviceId: undefined,
  },
  PIGELLO_PAYMENTS_CREATE: {
    applicationId: 'ed61eb81-7a28-4ad4-9dde-b65ac9a4ff4c',
    serviceId: '45896174-c25d-5e7e-b722-f164cf9345eb',
  },
  UPPSALA_BOSTADSFORMEDLING: {
    applicationId: 'fabe15b5-11ee-45f6-8263-c623059babc4',
    serviceId: '1ab3cae0-c4fd-500f-8b74-b7f039f761aa',
  },
  MICROSOFT_BOOKINGS: {
    applicationId: '7b01716f-51dc-427b-96a2-27f4b2f0c23f',
    serviceId: '5a4259f4-59dc-51eb-b033-c519387161fc',
  },
} as const satisfies IIntegrationServiceDataMap;

export const ApplicationIdToReadable: Record<string, string> = {
  '179cc7f5-9491-4e3c-8108-91f7e592a683': 'Fortnox',
  'ff7c49c6-9dee-4bb1-833f-dcd00c589207': 'SFTP',
  '4c52aeff-a913-407d-9f4b-b6087b49490b': 'Scrive',
  '8a9f2b67-cdce-4e56-98be-31a152c0d9cb': 'Autogiro',
  '72afc6a4-c437-4f57-a709-3273b71c7581': 'Autogiro Dev',
  '4ab874a5-b540-495c-bcc2-2ac7b4d3268d': 'Visma',
  'f74d684c-6b20-4f9e-b07c-9fed2692be2d': 'PS Finance',
  'e0ecd748-9aa9-472c-9b40-8ac69329ce54': 'FastDoc',
  '669e7ff7-a4d7-4f9d-801b-9c1e95dfa6ec': 'SCB',
  'e502aca0-8948-48a9-8dc7-47b99bb8020e': 'Infometric',
  'fabe15b5-11ee-45f6-8263-c623059babc4': 'Uppsala bostadsförmedling',
} as const;
